import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Layers, RefreshCw, CircleDot, Clock, Ban, CheckCircle2, Info } from 'lucide-react';
import { sw } from '../../lib/sitewalkApi';

const STATUS_STYLE = {
  FRESH: 'bg-emerald-100 text-emerald-700', STALE: 'bg-amber-100 text-amber-700',
  NEVER_CAPTURED: 'bg-slate-200 text-slate-600',
};
const ageLabel = (a) => (a === null || a === undefined ? 'Never captured' : a === 0 ? 'Captured today' : `Last captured ${a} day${a === 1 ? '' : 's'} ago`);

export default function CoverageDashboard({ projectId }) {
  const [cov, setCov] = useState(null);
  const [sched, setSched] = useState(null);
  const [ev, setEv] = useState(null);
  const [kind, setKind] = useState('zone');
  const [staleDays, setStaleDays] = useState(14);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, e] = await Promise.all([
        sw.coverageCapture(projectId, { kind, staleDays }),
        sw.coverageSchedule(projectId), sw.coverageEvidence(projectId),
      ]);
      setCov(c); setSched(s); setEv(e);
    } catch (err) { toast.error('Failed to load coverage'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, kind, staleDays]);

  const kpis = cov ? [
    { label: `Configured ${kind}s`, value: cov.totalAreas, icon: Layers, tone: 'text-slate-700' },
    { label: 'Captured', value: cov.captured, icon: CheckCircle2, tone: 'text-sky-700' },
    { label: 'Fresh', value: cov.fresh, icon: CircleDot, tone: 'text-emerald-700' },
    { label: 'Stale', value: cov.stale, icon: Clock, tone: 'text-amber-700' },
    { label: 'Never captured', value: cov.neverCaptured, icon: Ban, tone: 'text-slate-500' },
    { label: 'Activities w/ 360', value: sched?.activitiesWithEvidence ?? '–', icon: CheckCircle2, tone: 'text-sky-700' },
    { label: 'Records linked to 360', value: ev?.linkedRecords ?? '–', icon: CheckCircle2, tone: 'text-violet-700' },
  ] : [];

  // group areas by building > floor
  const groups = {};
  (cov?.areas || []).forEach((a) => {
    const b = a.buildingName || 'Unassigned building';
    const f = a.floorName || 'Unassigned floor';
    groups[b] = groups[b] || {};
    groups[b][f] = groups[b][f] || [];
    groups[b][f].push(a);
  });

  return (
    <div data-testid="coverage-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">Area type</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="border rounded-lg px-2 py-1 text-xs" data-testid="coverage-kind-select">
            <option value="zone">Zone</option><option value="floor">Floor</option><option value="building">Building</option><option value="space">Space</option>
          </select>
          <label className="text-xs text-slate-500 ml-2">Stale after (days)</label>
          <input type="number" value={staleDays} min={1} onChange={(e) => setStaleDays(Number(e.target.value) || 14)} className="border rounded-lg px-2 py-1 text-xs w-16" data-testid="coverage-staledays-input" />
        </div>
        <button onClick={load} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg border text-slate-600" data-testid="coverage-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      {loading ? <p className="text-sm text-slate-400">Loading coverage…</p> : (
        <>
          <p className="text-sm font-semibold text-slate-700 mb-2" data-testid="coverage-label">{cov?.capturedLabel}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 mb-2" data-testid="coverage-kpis">
            {kpis.map((k) => (
              <div key={k.label} className="bg-white rounded-xl border border-slate-200 p-3">
                <k.icon className={`w-4 h-4 ${k.tone}`} />
                <div className={`text-xl font-bold mt-1 ${k.tone}`} data-testid={`kpi-${k.label.replace(/[^a-z]/gi, '-').toLowerCase()}`}>{k.value}</div>
                <div className="text-[10px] text-slate-500">{k.label}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 flex items-center gap-1 mb-4"><Info className="w-3.5 h-3.5" /> {cov?.governanceNote}</p>

          <div className="space-y-3" data-testid="coverage-drilldown">
            {Object.entries(groups).map(([b, floors]) => (
              <div key={b} className="bg-white rounded-xl border border-slate-200">
                <div className="px-3 py-2 border-b text-sm font-semibold text-slate-700">{b}</div>
                {Object.entries(floors).map(([f, areas]) => (
                  <div key={f} className="px-3 py-2 border-b last:border-0">
                    <div className="text-xs font-medium text-slate-500 mb-1.5">{f}</div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                      {areas.map((a) => (
                        <div key={a.locationId} className="flex items-center justify-between rounded-lg border border-slate-100 px-2.5 py-1.5" data-testid={`coverage-area-${a.locationId}`}>
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-slate-700 truncate">{a.name}</div>
                            <div className="text-[10px] text-slate-400">{ageLabel(a.ageDays)} · {a.captureCount} capture{a.captureCount === 1 ? '' : 's'}</div>
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${STATUS_STYLE[a.status]}`}>{a.status === 'NEVER_CAPTURED' ? 'Never' : a.status[0] + a.status.slice(1).toLowerCase()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
            {(cov?.areas || []).length === 0 && <p className="text-sm text-slate-400">No areas configured for coverage. Define zones in Construction Structure.</p>}
          </div>
        </>
      )}
    </div>
  );
}
